import { CurrencyPipe, KeyValuePipe } from '@angular/common'
import { HttpParams } from '@angular/common/http'
import { Component } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { WjChartModule } from '@grapecity/wijmo.angular2.chart'
import { WjChartAnnotationModule } from '@grapecity/wijmo.angular2.chart.annotation'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { CellEditEndingEventArgs, CellRangeEventArgs, CellType, FlexGrid, GridPanel } from '@grapecity/wijmo.grid'
import { NgbNav, NgbNavContent, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavOutlet } from '@ng-bootstrap/ng-bootstrap'

import { BaseComponent, FeatureType, Grid, ListItem, ModalSize, SafetyMitigation } from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridSelectionCalculationComponent } from '../../shared/reusable-components/grid-selection-calculation/grid-selection-calculation.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { SafetyMitigationComponent } from './safety-mitigation.component'

@Component({
    templateUrl: './safety.component.html',
    standalone: true,
    imports: [
        NgbNav,
        NgbNavItem,
        NgbNavItemRole,
        NgbNavLink,
        NgbNavLinkBase,
        NgbNavContent,
        FormsModule,
        WjInputModule,
        WjChartModule,
        WjChartAnnotationModule,
        AutoSizeDirective,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        WjGridModule,
        WjGridFilterModule,
        GridSelectionCalculationComponent,
        NgbNavOutlet,
        CurrencyPipe,
        KeyValuePipe,
    ],
})
export class SafetyComponent extends BaseComponent {
    faSearch = faSearch
    active = 1
    mitigationGrid: Grid<SafetyMitigation>
    incidentGrid: Grid<any>
    vpIncidentGrid: Grid<any>
    timeSeries: any[] = []
    maxIncidentRange = 0
    maxIncurredRange = 0
    timeunits: ListItem<number>[] = []
    selectedTimeunit = 6
    demographicsByType = {} as any
    vpDemographicsByType = {} as any
    demographicStartDate: Date
    demographicEndDate: Date
    noHrrefCount = 0
    vpNoHrrefCount = 0
    incidentMonthChartData = [] as any[]

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.HrSafetyEdit])
    }

    constructor() {
        super()
        const now = new Date().date()
        this.demographicStartDate = new Date(2000, 0, 1)
        this.demographicEndDate = now

        this.mitigationGrid = new Grid<SafetyMitigation>(this.services)
            .setupModalEditing(
                this.services.data.safetyMitigations,
                'Safety Mitigation',
                SafetyMitigationComponent,
                () => new SafetyMitigation(),
                this,
                ModalSize.medium,
            )
            .filterBy(item => [item.description, item.chartLabel])

        this.incidentGrid = new Grid<any>(this.services)
            .isReadOnly(false)
            .wrapColumnHeaders()
            .filterBy(item => [item.name, item.stateAbbr])

        this.vpIncidentGrid = new Grid<any>(this.services)
            .isReadOnly(true)
            .wrapColumnHeaders()
            .filterBy(item => [item.name, item.stateAbbr])

        this.timeunits.push(new ListItem(5, 'Monthly'))
        this.timeunits.push(new ListItem(6, 'Quarterly'))
        this.timeunits.push(new ListItem(7, 'Yearly'))

        this.refresh()
    }

    refresh() {
        const params = new HttpParams()
            .set('timeUnit', this.selectedTimeunit)
            .set('demographicStartDate', this.demographicStartDate.toLocaleDateString())
            .set('demographicEndDate', this.demographicEndDate.toLocaleDateString())

        this.cancelableRequest(
            '',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.get('api/Safety/GetExcelData', params),
                    this.services.data.get('api/Safety/GetViewpointSafetyData', params),
                    this.services.data.safetyMitigations.queryAll(),
                )
                .subscribe({
                    next: result => {
                        this.initialLoadingComplete()
                        this.mitigationGrid.items = result[2]
                        this.incidentGrid.items = result[0].incidents
                        this.vpIncidentGrid.items = result[1].incidents
                        this.timeSeries = result[0].timeSeries
                        this.noHrrefCount = this.incidentGrid.items.filter(x => !x.hrRef).length

                        this.maxIncidentRange = 0
                        this.timeSeries.forEach(x => {
                            if (x.incidentCount > this.maxIncidentRange) this.maxIncidentRange = x.incidentCount
                        })
                        this.maxIncidentRange *= 1.25

                        this.maxIncurredRange = 0
                        this.timeSeries.forEach(x => {
                            if (x.incurrendAmount > this.maxIncurredRange) this.maxIncurredRange = x.incurrendAmount
                        })
                        this.maxIncurredRange *= 1.25

                        this.processAnnotationLocation()

                        const demographics = result[0].demographics as any[]
                        this.demographicsByType = demographics.toObjectGroups(
                            x => x.mainDemo,
                            x => x,
                        )
                        this.vpNoHrrefCount = this.vpIncidentGrid.items.filter(x => !x.hrRef).length

                        this.vpDemographicsByType = result[1].demographics.toObjectGroups(
                            x => x.mainDemo,
                            x => x,
                        )

                        // We have enough data on the client now to get incident by month.
                        // While we could calculate this on the server, it would just be more data to push.
                        this.processIncidentByMonth()
                    },
                }),
        )
    }

    processIncidentByMonth() {
        const data = [
            { month: 'Jan', affinity: 0, viewpoint: 0 },
            { month: 'Feb', affinity: 0, viewpoint: 0 },
            { month: 'Mar', affinity: 0, viewpoint: 0 },
            { month: 'Apr', affinity: 0, viewpoint: 0 },
            { month: 'May', affinity: 0, viewpoint: 0 },
            { month: 'Jun', affinity: 0, viewpoint: 0 },
            { month: 'Jul', affinity: 0, viewpoint: 0 },
            { month: 'Aug', affinity: 0, viewpoint: 0 },
            { month: 'Sep', affinity: 0, viewpoint: 0 },
            { month: 'Oct', affinity: 0, viewpoint: 0 },
            { month: 'Nov', affinity: 0, viewpoint: 0 },
            { month: 'Dec', affinity: 0, viewpoint: 0 },
        ]
        let items = this.incidentGrid.items
        for (const item of items) {
            data.filter(x => x.month == item.accidentDate.toLocaleString('en-US', { month: 'short' }))[0].affinity++
        }
        items = this.vpIncidentGrid.items
        for (const item of items) {
            data.filter(x => x.month == item.accidentDate.toLocaleString('en-US', { month: 'short' }))[0].viewpoint++
        }
        this.incidentMonthChartData = data
    }

    processAnnotationLocation() {
        for (const item of this.services.data.safetyMitigations.items) {
            let match
            switch (this.selectedTimeunit) {
                case 5:
                    match = this.timeSeries.find(x => x.date === item.dateLabelMonth)
                    break
                case 6:
                    match = this.timeSeries.find(x => x.date === item.dateLabelQuarter)
                    break
                case 7:
                    match = this.timeSeries.find(x => x.date === item.dateLabelYear)
                    break
                default:
            }
            if (match) item.pointIndex = this.timeSeries.indexOf(match)
        }
    }

    onFileChanged(event) {
        const formData: FormData = new FormData()
        formData.append('files', event.target.files[0], 'Safety.xlsx')
        this.services.data.load(true, false, this.services.data.post('/api/Safety/UploadSafetyFile', formData)).subscribe({
            next: () => {
                this.refresh()
            },
            error: errorResult => {
                this.services.notification.error('The safety file could not be updated. ' + errorResult.error.join('. '))
            },
        })
    }

    incidentGridItemFormatter(panel: GridPanel, r, c, cell: HTMLDivElement) {
        if (panel.cellType === CellType.ColumnHeader) {
            // Set the header formatter
        }
        if (panel.cellType === CellType.Cell) {
            const row = panel.grid.rows[r]
            const col = panel.grid.columns[c]
            const rowItem = row.dataItem

            cell.classList.remove('yellow-cell')

            if (!rowItem.hasHrRefFromExcel && col.binding === 'hrRef') {
                cell.classList.add('yellow-cell')
            }
            if (col.binding == 'lossDescription') {
                cell.title = rowItem.lossDescription
            }
        }
    }

    cellEditEnding(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        if (args.cancel) return
        if (flexGrid.rows[args.row].dataItem.hasHrRefFromExcel) {
            args.cancel = true
            this.services.notification.info('Incidents with employee records looked up from the Excel file cannot be modified.')
        }
    }

    cellEditEnded(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        if (args.cancel) return
        args.cancel = true
        this.updateIncident(flexGrid.rows[args.row].dataItem)
    }

    pasting(flexGrid: FlexGrid, args: CellRangeEventArgs) {
        if (args.cancel) return
        args.cancel = true
        this.services.notification.info('Pasting is not supported in this field.')
    }

    updateIncident(item) {
        this.services.data.put('api/Safety/UpdateIncident', item).subscribe({
            next: () => {
                this.refresh()
            },
            error: () => {
                this.services.notification.error('The employee could not be found.')
                item.hrRef = 0
                this.incidentGrid.refresh()
            },
        })
    }

    tabChanged() {
        setTimeout(() => {
            this.processAnnotationLocation()
            window.dispatchEvent(new Event('resize'))
            //this.incidentGrid.flexGrid.autoSizeColumns();
        }, 100)
    }
}
